import { useEffect, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Voiding a posted document, the same way on every screen.
 *
 * Nothing posted is ever deleted. The document stays, marked cancelled, and a
 * reversing entry undoes its money (and its stock, where it had any) on today's
 * date — so the books still balance, last month still reads as it did, and the
 * reason is kept beside it for whoever asks later. Only the owner or an
 * administrator is offered this at all.
 */
export function VoidDialog({
  docNo,
  what,
  effect,
  busy,
  onCancel,
  onConfirm,
}: {
  /** The document being voided, or null when the dialog is closed. */
  docNo: string | null;
  /** "expense", "receipt", "invoice" — used in the title and button. */
  what: string;
  /** One sentence on what reverses, in this screen's own terms. */
  effect: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (docNo === null) setReason('');
  }, [docNo]);

  return (
    <ConfirmDialog
      open={docNo !== null}
      tone="danger"
      title={`Void ${what} ${docNo ?? ''}?`}
      message={`${effect} The ${what} itself stays on record, marked cancelled.`}
      detail={
        <label className="block text-sm">
          <span className="mb-1 block text-2xs text-ink-soft">Reason (kept on the record)</span>
          <input
            className="field w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Entered twice by mistake"
            autoComplete="off"
            autoFocus
          />
        </label>
      }
      confirmLabel={`Void the ${what}`}
      confirmDisabled={reason.trim().length < 3}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(reason.trim())}
    />
  );
}
